import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { take } from 'rxjs/internal/operators/take';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { TagsDropAssignService } from '../../../../../shared/services/tags-drop-assign.service';
import { SendReportFormComponent } from '../../send-report-form/send-report-form.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
	selector: 'app-clips-toolbar',
	standalone: true,
	imports: [MatToolbarModule, MatDividerModule, MatButtonModule, TranslateModule, MatTooltipModule, MatIconModule],
	templateUrl: './clips-toolbar.component.html',
	styleUrl: './clips-toolbar.component.scss',
})
export class ClipsToolbarComponent implements OnChanges {
  	@Input() scrollToTop: boolean = false;
	@Input() totalItemsCount: number = 0;
	@Input() selectedItems: any;
	@Input() relatedClipsLoading: boolean = false;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	constructor(private dialog: MatDialog, public utils: UtilsService, public tagsDropAssign: TagsDropAssignService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedItems']) {
			this.selectedItems = changes['selectedItems'].currentValue;
		}
	}

	checkValidity(type: 'MERGE_GROUP' | 'CREATE_GROUP' | 'ADD_TAGS' | 'COPY_TAGS' | 'COPY_GROUP' | 'GENERATE_REPORT' | 'UNLINK_FROM_GROUP' | 'SEND_TO_CLIENTS' | 'DELETE_DUPLICATES' | 'DELETE') {
		switch (type) {
			case 'MERGE_GROUP': {
				return this.selectedItems.length && this.selectedItems.find((item: any) => item.main);
			}
			case 'CREATE_GROUP': {
				return this.selectedItems.length && this.selectedItems.every((item: any) => !item.main);
			}
			case 'ADD_TAGS': {
				return this.selectedItems.length && this.tagsDropAssign.manuallyselectedTags && this.tagsDropAssign.manuallyselectedTags.length;
			}
			case 'COPY_TAGS': {
				if (!this.selectedItems.length) {
					return false;
				}
				
				const uniqueGroups = new Map();
				this.selectedItems.forEach((item: any) => {
					if (!uniqueGroups.has(item.groupId)) {
						uniqueGroups.set(item.groupId, []);
					}
					uniqueGroups.get(item.groupId).push(item);
				});
				
				if (uniqueGroups.size !== 2) {
					return false;
				}

				const groups = Array.from(uniqueGroups.values());
				const group1HasTags = groups[0].some((item: any) => item.tags && item.tags.length > 0);
				const group2HasTags = groups[1].some((item: any) => item.tags && item.tags.length > 0);
				
				return (group1HasTags && !group2HasTags) || (!group1HasTags && group2HasTags);
			}
			case 'COPY_GROUP': {
				return this.selectedItems.length && this.selectedItems.every((item: any) => this.selectedItems[0].groupId === item.groupId);
			}
			case 'GENERATE_REPORT':
			case 'SEND_TO_CLIENTS':
			case 'DELETE':
			case 'DELETE_DUPLICATES': {
				return this.selectedItems.length;
			}
			case 'UNLINK_FROM_GROUP': {
				if (!this.selectedItems.length) {
					return false;
				}

				const uniqueItems = this.getUniqueSelectedItems();
				const selectedMains = uniqueItems.filter((item: any) => item.main);

				// Related clips on their own can always be unlinked — their group keeps its main.
				if (!selectedMains.length) {
					return true;
				}

				// A main can only be unlinked along with its entire group. Unlinking it on its own
				// gives it a new groupId and leaves the remaining clips without a main, which drops
				// them out of the list altogether (the list query filters on main = true).
				return selectedMains.every((main: any) => {
					const selectedFromGroup = uniqueItems.filter((item: any) => item.groupId === main.groupId).length;
					return selectedFromGroup === (main.relatedCount || 0) + 1;
				});
			}
		}
	}

	/** Selection deduplicated by id — the same clip can be pushed twice when a group is select-all'd. */
	private getUniqueSelectedItems(): any[] {
		const checkDuplicates = new Set();
		const uniqueList: any[] = [];
		this.selectedItems.forEach((item: any) => {
			if (!checkDuplicates.has(item.id)) {
				uniqueList.push(item);
				checkDuplicates.add(item.id);
			}
		});
		return uniqueList;
	}

	getSelectedItemsCount() {
		return this.getUniqueSelectedItems().length;
	}

	itemAction(action: 'CREATE_GROUP' | 'COPY_GROUP' | 'MERGE_GROUP' | 'DELETE' | 'ADD_TAGS' | 'DELETE_DUPLICATES' | 'GENERATE_REPORT' | 'UNLINK_FROM_GROUP' | 'SEND_TO_CLIENTS' | 'SCROLL_TO_TOP' | 'COPY_TAGS' | 'UNSELECT_ALL') {
		if (action === 'SEND_TO_CLIENTS') {
			const dialogRef = this.dialog.open(SendReportFormComponent, {
				width: '600px',
				data: {
					selectedItems: this.selectedItems,
					dataAdministrationType: 'CLIPPING',
				},
			});
			// dialogRef
			// 	.afterClosed()
			// 	.pipe(take(1))
			// 	.subscribe(() => {
			// 		this.actionRequested.emit({ action: 'FILTER' });
			// 	});
		} else if (action === 'DELETE') {
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				width: '600px',
				data: {
					type: 'delete-confirmation',
					mode: 'DELETE',
				},
			});

			dialogRef
				.afterClosed()
				.pipe(take(1))
				.subscribe(result => {
					if (result) {
						this.actionRequested.emit({
							action: 'TOOLBAR',
							groupAction: action,
						});
					}
				});
		} else if (action === 'UNSELECT_ALL') {
			this.actionRequested.emit({
				action: 'UNSELECT_ALL',
			});
		} else {
			this.actionRequested.emit({
				action: 'TOOLBAR',
				groupAction: action,
				tags: this.tagsDropAssign.manuallyselectedTags,
			});
		}
	}
}
